import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { getAuth } from 'firebase/auth';
import { useNavigate, useLocation } from 'react-router-dom';
import { FaArrowLeft } from 'react-icons/fa';
import { AudioContext } from '../context/AudioContext';
import TrackItem from './TrackItem';
import '../styles/LikedTracks.css';

const LikedTracks = () => {
  const { handleTrackClick } = useContext(AudioContext);
  const location = useLocation();
  const navigate = useNavigate();
  const auth = getAuth();
  const initialTracks = location.state?.tracks || [];
  const [likedTracks, setLikedTracks] = useState(initialTracks);
  const [loading, setLoading] = useState(!initialTracks.length);
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const token = await user.getIdToken();
          const response = await axios.get(
            `https://sync-api-troz.onrender.com/api/users/firebase/${user.uid}`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setPlan(response.data.plan);
        }
      } catch (error) {
        console.error('Error fetching user plan:', error);
      }
    };

    fetchUserPlan();
  }, [auth.currentUser]);

  useEffect(() => {
    const fetchLikedTracks = async () => {
      if (!initialTracks.length) {
        setLoading(true);
        try {
          const response = await axios.get('https://sync-api-troz.onrender.com/api/uploads/likes', {
            params: { firebaseUid: auth.currentUser?.uid },
          });
          setLikedTracks(response.data);
        } catch (error) {
          console.error('Error fetching liked tracks:', error.response ? error.response.data : error.message);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchLikedTracks();
  }, [auth, initialTracks]);

  const handleStarTrack = async (trackId) => {
    const firebaseUid = auth.currentUser?.uid;
    if (!firebaseUid) {
      window.alert('You must be logged in to star a track.');
      return;
    }

    const track = likedTracks.find((t) => t._id === trackId);
    if (track?.user?.firebaseUid === firebaseUid) {
      window.alert("You can't star your own track.");
      return;
    }

    try {
      await axios.post('https://sync-api-troz.onrender.com/api/uploads/star', {
        trackId,
        userId: firebaseUid,
      });
      setLikedTracks((prevTracks) =>
        prevTracks.map((track) =>
          track._id === trackId
            ? {
                ...track,
                stars: (track.stars || 0) + 1,
                starredBy: [...(track.starredBy || []), firebaseUid],
              }
            : track
        )
      );
      window.alert('Track starred successfully!');
    } catch (error) {
      if (error.response?.data?.message === 'You have already starred a track from this artist') {
        window.alert('You already starred a track from this user.');
      } else {
        console.error('Error starring track:', error.response || error.message);
        window.alert('An error occurred while starring the track.');
      }
    }
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className="liked-tracks-loading">
          <div className="loader"></div>
          <p>Loading Liked Tracks...</p>
        </div>
      );
    }

    if (likedTracks.length === 0) {
      return <p className="no-tracks-text">No liked tracks to display</p>;
    }

    return likedTracks.map((track) => (
      <TrackItem
        key={track._id}
        track={track}
        onPlay={() => handleTrackClick(track)}
        onStar={() => handleStarTrack(track._id)}
        canPin={false}
        isOwner={false}
        currentUserId={auth.currentUser?.uid}
      />
    ));
  };

  return (
    <div className="liked-tracks-container">
      <div className="header-liked-tracks">
        <button onClick={() => navigate(-1)} className="back-button">
          <FaArrowLeft size={24} color="#fff" />
        </button>
        <p className="header-text-liked-tracks">Liked Tracks</p>
        <div className="placeholder" />
      </div>
      <div className="scroll-content-liked-tracks">{renderContent()}</div>
    </div>
  );
};

export default LikedTracks;